import type { EvolutionId } from './game/systems/evolutions';
import type { RunState } from './game/types';

const KEY = 'cradle7.runResume';
const SCHEMA_VERSION = 10; // bumped in Phase 3m: RunState gained level/xp/pendingLevelUps/sectorDurationSec/sectorTimeLeftSec

/**
 * The on-disk shape mirrors RunState except `appliedEvolutions` is stored as
 * an array (JSON has no native Set; a Set stringifies to {} and would round-trip
 * to lost data). Convert at both boundaries.
 */
type StoredRunState = Omit<RunState, 'appliedEvolutions'> & {
  appliedEvolutions: EvolutionId[];
};

function toStorable(state: RunState): StoredRunState {
  return {
    ...state,
    appliedEvolutions: Array.from(state.appliedEvolutions),
  };
}

function fromStorable(stored: StoredRunState): RunState {
  return {
    ...stored,
    appliedEvolutions: new Set<EvolutionId>(stored.appliedEvolutions ?? []),
  };
}

/** UTC yyyy-mm-dd today — matches the server's challenge date convention. */
function todayUtcIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Phase 2i H2 fix: drop challengeMode if its date isn't today.
 *
 * Without this scrub, resuming a run started yesterday keeps challengeMode
 * pointing at the previous day's challenge. On submit the server would
 * silent-drop it (date mismatch → no challenge_entries write, no streak,
 * no fishies), and the player would lose the entire run with no signal.
 * Reverting to a non-challenge run is the least-bad outcome.
 */
export function scrubStaleChallengeMode(state: RunState, today: string = todayUtcIso()): RunState {
  if (state.challengeMode && state.challengeMode.date !== today) {
    return { ...state, challengeMode: null };
  }
  return state;
}

/** Phases that represent a live, mid-flow run worth restoring. */
const RESUMABLE_PHASES: ReadonlyArray<RunState['phase']> = [
  'in_combat',
  'perk_choice',
  'level_up',
  'extracting',
];

type StoredEnvelope = {
  version: number;
  state: StoredRunState;
};

function isResumable(state: RunState): boolean {
  return RESUMABLE_PHASES.includes(state.phase);
}

export function saveResume(state: RunState): void {
  if (typeof window === 'undefined') return;
  if (!isResumable(state)) {
    try { window.localStorage.removeItem(KEY); } catch {}
    return;
  }
  const envelope: StoredEnvelope = { version: SCHEMA_VERSION, state: toStorable(state) };
  try {
    window.localStorage.setItem(KEY, JSON.stringify(envelope));
  } catch {
    /* quota — accept the loss */
  }
}

export function loadResume(): RunState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredEnvelope> | RunState;
    // Recognize envelope vs legacy unwrapped state (defensive — pre-v1
    // saves may still exist in users' localStorage from the prior release).
    if (
      parsed &&
      typeof parsed === 'object' &&
      'version' in parsed &&
      'state' in parsed &&
      (parsed as StoredEnvelope).version === SCHEMA_VERSION &&
      (parsed as StoredEnvelope).state
    ) {
      return fromStorable((parsed as StoredEnvelope).state);
    }
    // Unknown version or no envelope → discard (treat as if no save existed).
    try { window.localStorage.removeItem(KEY); } catch {}
    return null;
  } catch {
    return null;
  }
}

export function clearResume(): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.removeItem(KEY); } catch {}
}

export function hasResumableRun(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(KEY) !== null;
  } catch {
    return false;
  }
}
